// Single frontend home for unit display labels. Handles BOTH vocabularies that
// reach the UI so no surface ever echoes a raw code (Polish-first rule):
//   - lowercase sale-unit / WZ codes ('pcs', 'm2', ...) — mirrors backend
//     unitLabels.js (the document-side source of truth)
//   - the uppercase `ProductUnit` enum ('PIECE', 'SQUARE_METER', ...) stored as
//     a product's baseUnit and carried onto PZ lines.
import { normalizeLanguageCode } from './numberFormatting';

/**
 * `ProductUnit` enum -> canonical lowercase line code. EXACT mirror of
 * PRODUCT_UNIT_TO_LINE_UNIT in backend/src/utils/unitLabels.js — the screen and
 * the PDF group and total quantities through this map, so a divergence prints
 * two different units for one number on the same document.
 *
 * SET / PACKAGE deliberately collapse to pieces on a *line code* (a WZ line
 * cannot release "half a set"), even though formatUnit still labels the raw
 * enum as 'kpl.' / 'opak.' where a product's own baseUnit is displayed.
 */
const PRODUCT_UNIT_TO_LINE_UNIT: Record<string, string> = {
  PIECE: 'pcs',
  KILOGRAM: 'kg',
  METER: 'm',
  LINEAR_METER: 'mb',
  SQUARE_METER: 'm2',
  CUBIC_METER: 'm3',
  LITER: 'l',
  SET: 'pcs',
  PACKAGE: 'pcs',
};

const PL_UNIT_LABELS: Record<string, string> = {
  // lowercase sale-unit / WZ line codes
  pcs: 'szt.',
  m2: 'm²',
  m3: 'm³',
  kg: 'kg',
  mb: 'mb',
  m: 'm',
  l: 'l',
  // ProductUnit enum (uppercase) — product baseUnit, e.g. on PZ item lines
  PIECE: 'szt.',
  KILOGRAM: 'kg',
  METER: 'm',
  LINEAR_METER: 'mb',
  SQUARE_METER: 'm²',
  CUBIC_METER: 'm³',
  LITER: 'l',
  SET: 'kpl.',
  PACKAGE: 'opak.',
};

const EN_UNIT_LABELS: Record<string, string> = {
  ...PL_UNIT_LABELS,
  pcs: 'pcs',
  PIECE: 'pcs',
  mb: 'lm',
  LINEAR_METER: 'lm',
  SET: 'set',
  PACKAGE: 'pkg',
};

/**
 * Normalize either vocabulary to a canonical lowercase line code, or null when
 * the value is unknown/empty. Mirrors backend unitLabels.js normalizeUnitCode.
 *
 * Use this wherever quantities are GROUPED or CONVERTED. For display, prefer
 * formatUnit on the raw value — it keeps the richer 'kpl.' / 'opak.' labels that
 * the line-code collapse throws away.
 */
export function normalizeUnitCode(unit: string | null | undefined): string | null {
  if (typeof unit !== 'string') return null;

  const trimmed = unit.trim();
  if (!trimmed) return null;

  if (Object.prototype.hasOwnProperty.call(PRODUCT_UNIT_TO_LINE_UNIT, trimmed.toUpperCase())) {
    return PRODUCT_UNIT_TO_LINE_UNIT[trimmed.toUpperCase()];
  }

  const lowered = trimmed.toLowerCase();
  // 'szt.' / 'szt' reach here from legacy free-text rows; dimensionConversion's
  // normalizeQuantityUnit accepts them too, so this stays in step with it.
  if (['szt.', 'szt', 'piece', 'pieces'].includes(lowered)) return 'pcs';

  return Object.prototype.hasOwnProperty.call(PL_UNIT_LABELS, lowered) ? lowered : null;
}

export function formatUnit(
  unit: string | null | undefined,
  language = 'pl',
): string {
  const labels = normalizeLanguageCode(language) === 'en' ? EN_UNIT_LABELS : PL_UNIT_LABELS;
  if (!unit) return labels.pcs;

  // hasOwnProperty on BOTH lookups: `unit` is stored operator input, and a bare
  // index would return Object.prototype members ('constructor' -> a function)
  // straight into JSX.
  const own = (key: string): string | undefined => (
    Object.prototype.hasOwnProperty.call(labels, key) ? labels[key] : undefined
  );

  return own(unit) || own(normalizeUnitCode(unit) || '') || unit;
}
